import { Component, DestroyRef, ViewEncapsulation, computed, effect, inject, input, output } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CfiRecordViewComponent, StudentAwardService } from '@k12/app-components';
import { LoadingSpinnerService } from '@k12/app-services';
import { SOS_SECTIONS, SOS_OPTIONS } from '../student-os.config';
import { StudentOs, ProgramType, ProgramTypes } from '@k12/models';
import { createEnrollmentApply } from '../../enrollment-apply';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'student-os-detail',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [ReactiveFormsModule, CfiRecordViewComponent],
  template: `
    <cfi-record-view
      title="Opportunity Scholarship"
      [sections]="sections"
      [form]="form"
      [options]="options"
      [canEdit]="isAdmin()"
      [showResendAwardOfferButton]="showResendAwardOffer()"
      [showEmptyState]="showEmptyState()"
      [emptyStateButtonDisabled]="applyDisabled()"
      [showPrintButton]="isAdmin() && !!studentOs()"
      prefix="sos"
      (edit)="onEdit()"
      (print)="onPrintApplication()"
      (emptyStateApply)="onEmptyStateApply()"
      (resendAwardOffer)="onResendAwardOffer()" />
  `
})
export class StudentOsDetailComponent {
  readonly isAdmin = input(false);
  readonly studentOs = input<StudentOs | null>(null);
  /** K12.Person id of the student — the application subject (Application.OnBehalfOfPersonId). */
  readonly personId = input<string | null>(null);
  /** The award year the household is viewing; used to probe/create the OS enrollment application. */
  readonly academicYearId = input<number | null>(null);
  readonly awardUpdated = output<StudentOs>();
  readonly printApplication = output<void>();

  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly studentAwardService = inject(StudentAwardService);
  private readonly spinnerService = inject(LoadingSpinnerService);
  private readonly destroyRef = inject(DestroyRef);

  readonly showEmptyState = computed(() => !this.studentOs());
  readonly showResendAwardOffer = computed(
    () => this.isAdmin() && (this.studentOs()?.osAwardStatus ?? '').toLowerCase() === 'unresponsive'
  );

  // Apply is only offered when the OS enrollment period for the selected year is open and the
  // application subject (personId) is known. Availability is a single reactive stream.
  private readonly applyController = createEnrollmentApply({
    programType: ProgramTypes.OsApplication,
    showEmptyState: this.showEmptyState,
    academicYearId: this.academicYearId,
    personId: this.personId
  });
  readonly applyDisabled = this.applyController.applyDisabled;

  readonly sections = SOS_SECTIONS;
  public options = {
    ...SOS_OPTIONS,
    inEligibilityReasons: [...SOS_OPTIONS['inEligibilityReasons']]
  };
  readonly form: FormGroup = this.fb.group(
    SOS_SECTIONS.flatMap((s) => s.fields).reduce<
      Record<string, [{ value: null; disabled: boolean } | null, ValidatorFn[]]>
    >((acc, field) => ({ ...acc, [field.key]: [field.readonly ? { value: null, disabled: true } : null, []] }), {})
  );

  private readonly studentId = this.route.snapshot.paramMap.get('studentId') ?? '';

  constructor() {
    this.populateDropdownOptions();
    effect(() => this.form.patchValue(this.studentOs() ?? {}));
  }

  private populateDropdownOptions(): void {
    const studentData = this.route.parent?.snapshot.data['student'] ?? this.route.snapshot.data['student'];

    const inEligibilityReasons = studentData?.inEligibilityReasons ?? [];
    this.options['inEligibilityReasons'] = [
      { label: 'None', value: null },
      ...inEligibilityReasons.map((reason: { name: string; id: number }) => ({
        label: reason.name,
        value: reason.id
      }))
    ];
  }

  onEmptyStateApply(): void {
    this.applyController.apply();
  }

  onEdit(): void {
    this.router.navigate(['/student', this.studentId, 'osedit']);
  }

  onResendAwardOffer(): void {
    this.spinnerService.showSpinner();
    this.studentAwardService
      .resendAwardOffer(this.studentId, ProgramType.OS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.spinnerService.hideSpinner();
          // Resend is only offered when an award record exists, so there is nothing to patch otherwise.
          const current = this.studentOs();
          if (!current) {
            return;
          }
          this.awardUpdated.emit({
            ...current,
            osAwardStatus: response.osAwardStatus ?? current.osAwardStatus
          });
        },
        error: () => this.spinnerService.hideSpinner()
      });
  }

  onPrintApplication(): void {
    this.printApplication.emit();
  }
}
